import React from "react";
import { Form, Select, DatePicker, Switch, Upload, message, Button, Spin } from "antd";
import moment from "moment";
import { UploadOutlined, LoadingOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import PremiumInput from "../../components/premium/PremiumInput";
import PremiumButton from "../../components/premium/PremiumButton";
import { uploadCampaignImage } from "../../services/campaign/campaignServices";
import { searchUsers, getUserById } from "../../services/user/userServices";

const { Option } = Select;

const CampaignForm = ({ initialValues, onFinish, loading, btnText = "Submit" }) => {
  const [form] = Form.useForm();
  const [uploading, setUploading] = React.useState(false);
  const [imageUrl, setImageUrl] = React.useState(initialValues?.imageUrl || "");
  const [users, setUsers] = React.useState([]);
  const [fetchingUsers, setFetchingUsers] = React.useState(false);

  const targetAudience = Form.useWatch("targetAudience", form);

  const handleUserSearch = React.useMemo(
    () =>
      debounce(async (query) => {
        if (!query || query.length < 3) return;
        setFetchingUsers(true);
        try {
          const res = await searchUsers(query);
          if (res?.success) {
            setUsers(res.data.users || []);
          }
        } catch (error) {
          console.error("User search error:", error);
        } finally {
          setFetchingUsers(false);
        }
      }, 800),
    []
  );

  // Prefetch existing users for Edit Mode
  React.useEffect(() => {
    const prefetchUsers = async () => {
      if (initialValues?.targetUserIds?.length > 0) {
        setFetchingUsers(true);
        try {
          const fetchedUsers = await Promise.all(
            initialValues.targetUserIds.map(id => getUserById(id).then(res => res?.success ? res.data : null))
          );
          const validUsers = fetchedUsers.filter(u => u !== null);
          setUsers(prev => {
            const existingIds = new Set(prev.map(u => u.userId || u.id));
            const newUsers = validUsers.filter(u => !existingIds.has(u.userId || u.id));
            return [...prev, ...newUsers];
          });
        } catch (error) {
          console.error("Prefetch error:", error);
        } finally {
          setFetchingUsers(false);
        }
      }
    };
    prefetchUsers();
  }, [initialValues]);

  const handleImageUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    setUploading(true);
    try {
      const res = await uploadCampaignImage(file);
      if (res?.success) {
        const url = res.data.fileUrl;
        form.setFieldsValue({ imageUrl: url });
        setImageUrl(url);
        message.success("Image uploaded successfully");
        onSuccess("ok");
      } else {
        message.error(res?.message || "Upload failed");
        onError("Upload failed");
      }
    } catch (error) {
      message.error("An error occurred during upload");
      onError(error);
    } finally {
      setUploading(false);
    }
  };

  // Handle initial values for DatePicker
  const formattedInitialValues = initialValues ? {
    ...initialValues,
    startDate: initialValues.startDate ? moment(initialValues.startDate) : null,
    endDate: initialValues.endDate ? moment(initialValues.endDate) : null,
  } : {};

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={formattedInitialValues}
      onFinish={(values) => {
        const payload = {
          ...values,
          startDate: values.startDate ? values.startDate.toISOString() : null,
          endDate: values.endDate ? values.endDate.toISOString() : null,
        };
        onFinish(payload);
      }}
    >
      <Form.Item
        label="Campaign Title"
        name="title"
        rules={[{ required: true, message: "Please enter campaign title" }]}
      >
        <PremiumInput placeholder="Enter campaign title" />
      </Form.Item>

      <Form.Item
        label="Campaign Body"
        name="body"
        rules={[{ required: true, message: "Please enter campaign body" }]}
      >
        <PremiumInput.TextArea placeholder="Enter campaign description" rows={4} />
      </Form.Item>

      <Form.Item
        label="Campaign Image"
        name="imageUrl"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <PremiumInput 
                placeholder="https://example.com/image.png" 
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
              />
            </div>
            <Upload
              customRequest={handleImageUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={uploading ? <LoadingOutlined /> : <UploadOutlined />} disabled={uploading}>
                {uploading ? "Uploading..." : "Upload Image"}
              </Button>
            </Upload>
          </div>
          {imageUrl && (
            <div style={{ 
              width: "100%", 
              maxHeight: "200px", 
              overflow: "hidden", 
              borderRadius: "8px",
              border: "1px solid #f0f0f0",
              textAlign: "center"
            }}>
              <img 
                src={imageUrl} 
                alt="Campaign Preview" 
                style={{ maxWidth: "100%", height: "auto", objectFit: "contain" }} 
              />
            </div>
          )}
        </div>
      </Form.Item>

      <Form.Item
        label="Click Action (URL/Route)"
        name="clickAction"
      >
        <PremiumInput placeholder="Where should users go when they click?" />
      </Form.Item>

      <div style={{ display: "flex", gap: "16px" }}>
        <Form.Item
          label="Start Date"
          name="startDate"
          rules={[{ required: true, message: "Required" }]}
          style={{ flex: 1 }}
        >
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          label="End Date"
          name="endDate"
          rules={[{ required: true, message: "Required" }]}
          style={{ flex: 1 }}
        >
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>
      </div>

      <div style={{ display: "flex", gap: "16px" }}>
        <Form.Item
          label="Target Audience"
          name="targetAudience"
          initialValue="ALL"
          style={{ flex: 1 }}
        >
          <Select>
            <Option value="ALL">All Users</Option>
            <Option value="SPECIFIC">Specific Users</Option>
          </Select>
        </Form.Item>

        {targetAudience === "SPECIFIC" && (
          <Form.Item
            label="Select Users"
            name="targetUserIds"
            rules={[{ required: true, message: "Please select at least one user" }]}
            style={{ flex: 2 }}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="Search by name or email"
              filterOption={false}
              onSearch={handleUserSearch}
              notFoundContent={fetchingUsers ? <Spin size="small" /> : null}
              loading={fetchingUsers}
            >
              {users.map((user) => (
                <Option key={user.userId || user.id} value={user.userId || user.id}>
                  {user.fullName || user.userName} ({user.email || user.phoneNumber})
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          label="Priority"
          name="priority"
          initialValue="LOW"
          style={{ flex: 1 }}
        >
          <Select>
            <Option value="LOW">Low</Option>
            <Option value="MEDIUM">Medium</Option>
            <Option value="HIGH">High</Option>
          </Select>
        </Form.Item>
      </div>

      <Form.Item
        label="Send Push Notification on Start?"
        name="sendPushOnStart"
        valuePropName="checked"
        initialValue={true}
      >
        <Switch />
      </Form.Item>

      <Form.Item>
        <PremiumButton type="primary" htmlType="submit" loading={loading} block>
          {btnText}
        </PremiumButton>
      </Form.Item>
    </Form>
  );
};

export default CampaignForm;
